import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, StatCard, Tag } from "@/components/common";
import { HardDrives, Cpu, GraphicsCard, Database, Sparkle, Plug, Flask, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOCAL_STATUS_COLORS = {
  "LOCAL READY": "#00FF9D", "LOCAL RUNNING": "#00FF9D", "CLOUD LIVE": "#00E5FF",
  "LOCAL NOT INSTALLED": "#FFB800", "NOT ENOUGH VRAM": "#FF3366",
  "SELF-HOST SETUP REQUIRED": "#8B5CF6", "LOCAL ERROR": "#FF3366",
};
const MODES = [
  { id: "FREE_FIRST", label: "Free First", note: "Try Studio Local; ask before any paid provider" },
  { id: "BALANCED", label: "Balanced", note: "Mix local & cloud by quality/speed" },
  { id: "PREMIUM_FIRST", label: "Premium First", note: "Prefer premium cloud providers" },
  { id: "MANUAL", label: "Manual", note: "You choose the engine each time" },
];

export default function LocalAI() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [endpoints, setEndpoints] = useState({});

  const load = async () => {
    const { data } = await api.get("/local/status");
    setData(data);
    setEndpoints(Object.fromEntries(data.components.map((c) => [c.component, c.endpoint || ""])));
  };
  useEffect(() => { load(); }, []);

  const setMode = async (ai_mode) => {
    setData((d) => ({ ...d, ai_mode }));
    await api.put("/local/config", { ai_mode });
    toast.success(`AI mode: ${ai_mode.replace("_", " ")}`);
  };

  const saveEndpoint = async (comp) => {
    setSaving(true);
    try {
      await api.put("/local/config", { endpoints: { [comp]: endpoints[comp] } });
      await load();
      toast.success(endpoints[comp] ? "Self-hosted endpoint attached" : "Endpoint cleared");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (!data) return <Loading label="Detecting hardware & local models…" />;
  const hw = data.hardware;

  return (
    <div>
      <PageHeader icon={HardDrives} title="Local AI · Studio Local"
        subtitle="A real open-source production layer. This host has no GPU, so GPU models honestly show SELF-HOST SETUP REQUIRED — attach a self-hosted endpoint to enable them. No fake results."
        actions={<Link to="/local-lab" data-testid="to-test-lab" className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black"><Flask size={16} weight="fill" /> Test Lab</Link>} />

      {/* Hardware check */}
      <h3 className="font-display text-lg font-bold mb-4">Hardware Check</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard label="GPU" value={hw.gpu_detected ? hw.gpu.name : "None"} sub={hw.gpu_detected ? `${hw.vram_gb} GB VRAM` : "no CUDA GPU on host"} icon={GraphicsCard} accent={hw.gpu_detected ? "#00FF9D" : "#FF3366"} />
        <StatCard label="RAM" value={`${hw.ram_gb} GB`} sub={`${hw.ram_available_gb} GB free`} icon={Cpu} accent="#00E5FF" />
        <StatCard label="CPU cores" value={hw.cpu_cores} icon={Cpu} accent="#8B5CF6" />
        <StatCard label="Disk" value={`${hw.disk_free_gb} GB`} sub={`of ${hw.disk_total_gb} GB free`} icon={Database} accent="#FFB800" />
      </div>
      {!hw.gpu_detected && (
        <div className="mb-8 flex items-start gap-3 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/8 px-4 py-3">
          <Warning size={20} weight="fill" className="text-[#8B5CF6] mt-0.5" />
          <p className="text-sm text-zinc-300">No GPU detected on this host. Local video/voice/lip-sync/music need a GPU. Attach your own GPU server's endpoint below, or use the LIVE cloud providers. Image & STT already work via the cloud.</p>
        </div>
      )}

      {/* AI Mode */}
      <h3 className="font-display text-lg font-bold mb-4">AI Mode</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {MODES.map((m) => (
          <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
            className={`text-left rounded-xl border p-4 transition-colors ${data.ai_mode === m.id ? "border-[#00E5FF]/60 bg-[#00E5FF]/8" : "border-white/8 hover:border-white/20"}`}>
            <div className="font-semibold text-white text-sm">{m.label}</div>
            <div className="text-[11px] text-zinc-500 mt-1">{m.note}</div>
          </button>
        ))}
      </div>

      {/* Components */}
      <h3 className="font-display text-lg font-bold mb-4">Local Components</h3>
      <div className="space-y-4">
        {data.components.map((c) => {
          const col = LOCAL_STATUS_COLORS[c.status] || "#A1A1AA";
          const commercialOk = c.commercial_use.startsWith("ALLOWED");
          return (
            <Panel key={c.component} data-testid={`local-${c.component}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="lg:w-64 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-white">{c.name}</span>
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded border" style={{ color: col, borderColor: `${col}66` }}>{c.status}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">{c.model}{c.alternative !== "—" ? ` · alt: ${c.alternative}` : ""}</div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-zinc-400">{c.detail}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="text-[#00FF9D]">API cost: {c.api_cost}</span>
                    <span className="text-zinc-500">Compute: {c.compute}</span>
                    <span className="text-zinc-500">VRAM: {c.min_vram_gb}–{c.recommended_vram_gb} GB</span>
                    <Tag className="text-[10px]">{c.license}</Tag>
                    <span className={`inline-flex items-center gap-1 ${commercialOk ? "text-[#00FF9D]" : "text-[#FFB800]"}`}>
                      {commercialOk ? <CheckCircle size={12} weight="fill" /> : <Warning size={12} weight="fill" />}
                      {commercialOk ? "Commercial OK" : "Commercial: review licence"}
                    </span>
                  </div>
                </div>
              </div>
              {c.component !== "stt" && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <input data-testid={`endpoint-${c.component}`} value={endpoints[c.component] || ""} onChange={(e) => setEndpoints((p) => ({ ...p, [c.component]: e.target.value }))}
                    placeholder="Self-hosted GPU endpoint URL (e.g. https://my-gpu-server/generate)"
                    className="flex-1 rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#00E5FF]/50" />
                  <button data-testid={`attach-${c.component}`} onClick={() => saveEndpoint(c.component)} disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 px-4 py-2 text-sm text-white hover:border-[#00E5FF]/40 transition-colors">
                    <Plug size={14} weight="duotone" /> Attach
                  </button>
                </div>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
